import { Injectable } from '@angular/core';
import {
  OrderUL,
  COUNTRY_CODE,
  ValueAddedOP,
  ProductUL,
  TechnicalProductOP,
  ImSlump,
  OrderStatusEnum,
  ImUnloadType,
  Calendar,
} from '@cemex-core/cmx-api';
import { getLegalEntity } from '@cemex/cmx-client-layout';
import { createSelector, Store } from '@ngrx/store';
import {
  getBusinessSelectedStates,
  getLocationSelectedStates,
  getPurchaseOrderSelected,
  getSalesAreas,
  getShimpmentLocations,
  getShimpmentLocationsAndExperience,
  getSpecialInstructionsSelected,
} from '@ordersnproducts/modules/order-builder/modules/review/reducers/review.selectors';
import {
  getRmsRecipeSpecifications,
  getRmsSpecificationsRawMaterial,
  getValidVolumeAllocation,
} from '@ordersnproducts/modules/order-builder/modules/rms-specification/selectors';
import {
  getAddedValuesOpenPlatformState,
  getOpenPlatformProductAddedValues,
  getTemperatureVaps,
} from '@ordersnproducts/modules/order-builder/modules/rms-specification/selectors/added-value.selectors';
import { getOpenPlatformContracts } from '@ordersnproducts/modules/order-builder/modules/rms-specification/selectors/contracts.selectors';
import {
  getAvailableProducts,
  getOpenPlatformExtraProducts,
  getOpenPlatformProducts,
} from '@ordersnproducts/modules/order-builder/modules/rms-specification/selectors/products.selectors';
import {
  getSlotsResponse,
  getSlotWaitingListDefaulted,
  getOpenPlatformSlots,
  getSlotsErrors,
} from '@ordersnproducts/modules/order-builder/modules/rms-specification/selectors/slots.selectors';
import {
  getDcoEntitiesAsArray,
  getDcoIds,
  getDcoStatusState,
  getErrorSlotCode,
  getSelectedSlot,
  getSlotAndTravelTime,
  getSlotForFormID,
  getSpecialScenariosAvailable,
} from '@ordersnproducts/modules/shared/reducers/index';
import { SpecFieldNames } from '@ordersnproducts/modules/order-builder/modules/specification/constants/specification-field-names.enum';
import {
  getActiveExperienceCode,
  getFirstCalendar,
  getLocationSpecificationState,
  getPumpCapacities,
  getSelectedShippingConditions,
  getSummaryPage,
  OrderBuilderState,
  getInternalComments,
} from '@ordersnproducts/modules/order-builder/reducers';
import { _safe } from '@ordersnproducts/modules/shared/utils/safe-get.method';
import * as moment from 'moment';
import { filter, map, switchMap, take, withLatestFrom } from 'rxjs/operators';
import { ErrorCode } from '@ordersnproducts/modules/shared/models/slots-error-code.enum';
import { isPump } from '@ordersnproducts/modules/shared/utils/pump';
import { OrderItemUL } from '@cemex-core/cmx-api/models/order-items/order-item-ul';
import {
  getApplicationsAndCountryLang,
  getLoadSizes,
  getLocalSpacings,
  getSlumps,
  getSlumpsProduct,
  getTemperatures,
  getUnloadTimesWithLocalData,
  getUnloadTypesAndCountryLang,
} from '../../rms-specification/selectors/single-catalogs';
import { ORDER_EXPERIENCES } from '@ordersnproducts/modules/shared/constants/order-experiences';
import { TranslateService } from '@ngx-translate/core';
import { CommercialChannel } from '@ordersnproducts/modules/shared/models/commercial-channel.enum';
import {
  getModifyReasonCode,
  getModifyReasonComment,
} from '@ordersnproducts/modules/new-modify-order/selectors/modify-reason.selector';
import { makeReference } from '@ordersnproducts/utils/mix-reference';
import {
  ExperiencesService,
  TemperatureFeatures,
  VALUE_ADDED_SPECIALIZED_FEATURE,
} from '@ordersnproducts/modules/shared/services/experiences.service';
import { RecipeFormValues } from '../../retail-specification/models/form-values';
import { getOrderSelectedDetail } from '@ordersnproducts/modules/new-modify-order/selectors/order-selected-detail.selector';
import { UnloadTypePumpCodesFrance } from '@ordersnproducts/modules/shared/models/pump-type.enum';
import { UnloadTypeMixVeyorCodes } from '@ordersnproducts/modules/shared/models/mixed-veyor-type.enum';
import { getCutOff } from '../../rms-specification/selectors/cut-off.selectors';
import { combineLatest } from 'rxjs';
import { VL_COUNTRIES, VL_EXPERIENCES } from '../../location/models/point-of-delivery-various-location.enum';
import { getRefrigerantValueAdded } from '@ordersnproducts/utils/temperature.util';
import {
  PUMP_PRIMER_MIX,
  PUMP_PRIMER_SLUMP,
  PUMP_PRIMER_ARRIVAL_BEFORE_DELIVERY,
  PUMP_PRIMER_QUANTITY,
} from 'app/shared/pumping/pumping.constant';
import { getPumpPrimerState } from '@ordersnproducts/modules/shared/modules/pump-primer/redux/pump-primer.selectors';
import { UnloadTime } from '@cemex-core/cmx-api/models/order-management/unload-time';
import { CtdFacadeService } from '@ordersnproducts/modules/shared/modules/confirm-to-dispatch/ctd-facade.service';
import { AnalyticsTrackingService } from 'app/services/analytics-tracking.service';
import { prependedValueAdded } from '@ordersnproducts/modules/shared/utils/prepended-value-added';
import { generateAddedValueFormName } from '../../rms-specification/helpers/form-added-value-name-generator';
import { Spacing } from '@cemex-core/cmx-api/models/order-management/spacing';
import { isOrderWithPumpPrimerProduct } from '../../rms-specification/helpers/pump-primer-helper';
import { SpecialScenarioCode } from '@ordersnproducts/modules/shared/models/dco/order-status-modify-snapshot.model';
import { isNil } from 'lodash';
import { CtdOrderDispatchesResponse } from '@ordersnproducts/modules/shared/modules/confirm-to-dispatch/models/ctd-order-dispatches';
import { getNewModifyOrderState } from '@ordersnproducts/modules/new-modify-order/reducers';
import { SaveSpecialScenarioActive } from '@ordersnproducts/modules/shared/actions/special-scenarios.action';
import { FeatureToggleService } from '@cemex-core/cmx-rollout-toggle-v1';
import { Features } from '@ordersnproducts/features';
import { ModifiedFields } from '@ordersnproducts/modules/order-builder/modules/rms-summary/models/modified-fields.model';
import { BuildingMaterial } from '@ordersnproducts/modules/shared/modules/building-materials/components/building-materials/building-materials.component';

const addedValuesAndProducts = createSelector(
  getAddedValuesOpenPlatformState,
  getOpenPlatformProducts,
  (addedValuesState, availableProducts) => {
    return { addedValuesState, availableProducts };
  },
);

export const getData = createSelector(
  getOpenPlatformProducts,
  getSlumpsProduct,
  getActiveExperienceCode,
  (stateProducts, stateSlumps, experienceCode) => {
    return {
      stateProducts,
      stateSlumps,
      experienceCode,
    };
  },
);

export const getDataLegacy = createSelector(
  getAvailableProducts,
  getSlumpsProduct,
  getActiveExperienceCode,
  (stateProducts, stateSlumps, experienceCode) => {
    return {
      stateProducts,
      stateSlumps,
      experienceCode,
    };
  },
);

export interface TemperatureData {
  temperature: {
    temperatureId: number;
    temperatureCode: string;
  };
  summerSet?: {
    valueAddedCode: string;
  };
  refrigerant?: {
    valueAddedCode: string;
    valueAddedQuantity?: string;
  };
}
/**
 * Solves the Rms request
 */
@Injectable()
export class ULOrderRequestSolverService {
  constructor(
    private store: Store<OrderBuilderState>,
    private translateService: TranslateService,
    private experiencesService: ExperiencesService,
    private featureToggleService: FeatureToggleService,

    private ctdService: CtdFacadeService,
    private analyticsService: AnalyticsTrackingService,
  ) {}

  async createULOrderRequest(experienceCode?: ORDER_EXPERIENCES, isModify?: boolean): Promise<OrderUL> {
    const country = await this.getCountryCode();
    const recipes = await this.getRmsSpecificationRecipe();
    const experienceCommandWithOutDco =
      experienceCode === ORDER_EXPERIENCES.COMMAND_UNIVERSAL_LINK && !this.experiencesService.commandDcoEnabled();
    const createRmsOrderRequest = {
      purchaseOrder: await this.getPurchaseOrder(),
      deliveryDate:
        experienceCommandWithOutDco || experienceCode === ORDER_EXPERIENCES.SIMMA_UNIVERSAL_LINK
          ? await this.getDateTimeCommand(recipes[0].values)
          : await this.getDeliveryDateHeader(),
      specialInvoice: await this.isSpecialInvoice(),
      shippingCondition: await this.getShippingCondition(),
      contract: await this.getContract(country),
      contact: await this.getContact(isModify),
      productType: await this.getProductType(),
      customer: await this.getCustomer(),
      jobsite: await this.getJobsite(),
      pointOfDelivery: await this.getPointOfDelivery(country),
      instructions: await this.getInstrutions(),
      items: await this.createItem(experienceCode),
      commercialChannel: {
        commercialChannelCode: CommercialChannel.CEMEX_GO,
      },
      internalComments: await this.getInternalComments(),
    };

    if (!createRmsOrderRequest.internalComments) {
      delete createRmsOrderRequest.internalComments;
    }

    if (
      experienceCode === ORDER_EXPERIENCES.RMX_UNIVERSAL_LINK ||
      (ORDER_EXPERIENCES.COMMAND_UNIVERSAL_LINK && this.experiencesService.commandDcoEnabled())
    ) {
      createRmsOrderRequest['wTimeList'] = await this.getWaitingList();
      createRmsOrderRequest['av'] = await this.getSMultiplecheduleAvailability(recipes, false);
    }

    if (experienceCode === ORDER_EXPERIENCES.SIMMA_UNIVERSAL_LINK) {
      createRmsOrderRequest['salesArea'] = await this.getSalesArea();
    }

    // CTD Confirm to Dispatch (Unfortunately we are not able to inject values from isolated modules)
    if (this.ctdService.checkFeatureEnabled() && this.ctdService.checkExperience(experienceCode)) {
      const ctdValue = await this.ctdService.getOrderRequestConfirmToDispatch();

      if (ctdValue && (ctdValue as Partial<CtdOrderDispatchesResponse>).isConfirmToDispatchReq) {
        createRmsOrderRequest['confirmToDispatch'] = ctdValue;
        this.analyticsService.confirmToDispatch().confirmToDispatchNewOrder(createRmsOrderRequest);
      }
    }

    return createRmsOrderRequest as OrderUL;
  }

  protected async createItem(experienceCode?: ORDER_EXPERIENCES): Promise<OrderItemUL[]> {
    const country = await this.getCountryCode();
    const recipes = await this.getRmsSpecificationRecipe();
    const unloadTypePumpValues = await this.getUnloadTypePumpValues$();
    const items = [];
    let hasExtraProduct = false;
    let index = 0;
    for (const key of Object.keys(recipes)) {
      const formValues = recipes[key].values;
      const itemDraft = await this.createDraftProduct(experienceCode, formValues, index, key, country);
      const unloadType = _safe(() => itemDraft.unloadType.unloadTypeCode);
      const pumpPrimerProduct = _safe(() => formValues.pumpPrimerProduct);
      items.push(itemDraft);
      if (country === COUNTRY_CODE.USA) {
        if (isOrderWithPumpPrimerProduct(pumpPrimerProduct, unloadType, unloadTypePumpValues)) {
          let clonedItemDraft = this.cloneObject(itemDraft);
          clonedItemDraft.product = {
            productCode: formValues[PUMP_PRIMER_MIX],
            productDesc: '',
          };
          clonedItemDraft.slump = {
            slumpCode: formValues[PUMP_PRIMER_SLUMP],
          };
          clonedItemDraft.spacing = await this.getSpacing(undefined, formValues[PUMP_PRIMER_ARRIVAL_BEFORE_DELIVERY]);
          clonedItemDraft.quantity = formValues[PUMP_PRIMER_QUANTITY];
          clonedItemDraft['isPumpPrimer'] = true;

          const deliveryDateTimePumpPrimerItem = this.getDateTimePumpPrimerItem(
            clonedItemDraft.deliveryDate,
            clonedItemDraft.spacing,
          );
          const requestDateTimePumpPrimerItem = this.getDateTimePumpPrimerItem(
            clonedItemDraft.requestedDateTime,
            clonedItemDraft.spacing,
          );
          clonedItemDraft = {
            ...clonedItemDraft,
            deliveryDate: deliveryDateTimePumpPrimerItem,
            requestedDateTime: requestDateTimePumpPrimerItem,
          };
          items.push(clonedItemDraft);
        }
      }
      if (experienceCode === ORDER_EXPERIENCES.COMMAND_UNIVERSAL_LINK && !hasExtraProduct) {
        hasExtraProduct = true;
        const lenExtraProducts = await this.addExtraProductsToItems(formValues, itemDraft, items);
        index += lenExtraProducts;
      }
      index++;
    }
    return items;
  }

  private async addExtraProductsToItems(formValues: any, itemDraft: OrderItemUL, items: any[]) {
    try {
      const extraProducts = await this.getExtraProducts(formValues);
      if (extraProducts && extraProducts.length > 0) {
        for (const extraProduct of extraProducts) {
          const extraProductItem = {
            product: {
              productCode: extraProduct.foundProduct.productCode,
              productDesc: extraProduct.foundProduct.productDesc,
              concreteProductTypeCode: extraProduct.foundProduct.concreteProductTypeCode,
            } as ProductUL,
            quantity: extraProduct.quantity,
            loadQty: extraProduct.quantity,
            deliveryDate: itemDraft.deliveryDate,
            requestedDateTime: itemDraft.requestedDateTime,
          } as OrderItemUL;
          items.push(extraProductItem);
        }
      }
      return extraProducts.length;
    } catch (error) {
      console.error(`Error fetching extra products: ${error}`);
      return 0;
    }
  }

  /**
   * Solves the Rms request
   * @param experienceCode
   */
  private cloneObject<T>(obj: T): T {
    return { ...obj };
  }

  private async getRmsSpecificationRecipe() {
    return this.store
      .select(getRmsRecipeSpecifications)
      .pipe(
        take(1),
        map((recipes) => recipes),
      )
      .toPromise();
  }

  private async getInternalComments() {
    return this.store
      .select(getInternalComments)
      .pipe(
        take(1),
        map((state) => {
          return !!state ? state.selected : undefined;
        }),
      )
      .toPromise();
  }

  private async getUnloadTypePumpValues$() {
    return this.store
      .select(getPumpPrimerState)
      .pipe(
        take(1),
        map(({ unloadTypePumpValues }) => unloadTypePumpValues),
      )
      .toPromise();
  }

  protected async createDraftProduct(
    experienceCode: ORDER_EXPERIENCES,
    formValues: { [fieldName: string]: any },
    index: number,
    key: string,
    countryCode: string,
  ): Promise<OrderItemUL> {
    const experienceCommandWithDco =
      experienceCode === ORDER_EXPERIENCES.COMMAND_UNIVERSAL_LINK && this.experiencesService.commandDcoEnabled();
    const product = await this.products(formValues);
    const technicalProduct =
      experienceCode === ORDER_EXPERIENCES.RMX_UNIVERSAL_LINK ||
      experienceCode === ORDER_EXPERIENCES.ARKIK_UNIVERSAL_LINK
        ? await this.technicalProduct(formValues)
        : undefined;
    const selectedValueAddeds =
      experienceCode !== ORDER_EXPERIENCES.SIMMA_UNIVERSAL_LINK ? await this.getValueAddeds(formValues) : undefined;

    const temperatureData =
      (formValues[SpecFieldNames.TEMPERATURE_UNDER] !== undefined &&
        !formValues[SpecFieldNames.TEMPERATURE_UNDER_DISABLED]) ||
      (formValues[SpecFieldNames.TEMPERATURE_UNDER_DISABLED] && formValues[SpecFieldNames.SUMMER_SET]) ||
      formValues[SpecFieldNames.ICE_QUANTITY] !== undefined
        ? await this.getTemperatureData(formValues)
        : undefined;
    const selectedItem: OrderItemUL = {
      product: this.prods(experienceCode, product, technicalProduct, selectedValueAddeds, temperatureData),
      paymentTerm: {
        paymentTermCode: formValues[SpecFieldNames.PAYMENT_TERMS],
      },
      quantity: formValues[SpecFieldNames.QUANTITY],
      cleanUp: this.getCleanUp(formValues, countryCode),
      additionalInfo: formValues[SpecFieldNames.ADDITIONAL_INFO] && formValues[SpecFieldNames.ADDITIONAL_INFO].trim(),
    };

    if (experienceCode === ORDER_EXPERIENCES.RMX_UNIVERSAL_LINK) {
      selectedItem.requestedDateTime = await this.getRequestedDateByitem(formValues);
    } else if (experienceCommandWithDco || experienceCode === ORDER_EXPERIENCES.ARKIK_UNIVERSAL_LINK) {
      selectedItem.deliveryDate = await this.getDeliveryDateByItem(formValues);
      selectedItem.requestedDateTime = await this.getRequestedDateByitem(formValues);
    } else {
      selectedItem.deliveryDate = this.getDateTimeCommand(formValues);
      selectedItem.requestedDateTime = await this.getDateTimeCommand(formValues);
    }
    const commandDco =
      !!formValues[SpecFieldNames.SCHEDULE_MOMENT] &&
      experienceCode === ORDER_EXPERIENCES.COMMAND_UNIVERSAL_LINK &&
      this.experiencesService.commandDcoEnabled();
    const dcoFeatureEnabled = await this.experiencesService.experienceDcoEnabled$().pipe(take(1)).toPromise();
    let plant;
    if (dcoFeatureEnabled) {
      plant = await this.getPlantDCO();
    } else {
      plant = await this.getPlant();
    }
    // If the plant is not defined from the condition above then we are dealing with modify without DCO call
    // And for UK and USA we want same plant as it was before from order detail
    if (plant === undefined && (countryCode === COUNTRY_CODE.UNITED_KINGDOM || countryCode === COUNTRY_CODE.USA)) {
      plant = await this.getPlantFromOrderDetail();
    }
    // If the plant is not defined, we will use the plant from the calendar response to the simulate ul api
    if (plant === undefined && countryCode === COUNTRY_CODE.MEXICO) {
      plant = await this.getPlant();
    }
    if (
      commandDco ||
      (!!formValues[SpecFieldNames.SCHEDULE_MOMENT] &&
        experienceCode !== ORDER_EXPERIENCES.COMMAND_UNIVERSAL_LINK &&
        experienceCode !== ORDER_EXPERIENCES.SIMMA_UNIVERSAL_LINK)
    ) {
      if (plant) {
        selectedItem.plant = {
          plantCode: plant,
        };
      }
    }

    if (!!formValues[SpecFieldNames.PAYMENT_TERMS]) {
      selectedItem.paymentTerm = {
        paymentTermCode: formValues[SpecFieldNames.PAYMENT_TERMS],
      };
    }

    if (!!formValues[SpecFieldNames.APPLICATION]) {
      selectedItem.application = await this.getApplication(formValues);
    }

    if (!!formValues[SpecFieldNames.UNLOAD_TYPE]) {
      selectedItem.unloadType = await this.getUnloadType(formValues, experienceCode);
    }

    if (!!formValues[SpecFieldNames.UNLOAD_TIME]) {
      selectedItem.unloadTime = await this.getUnloadTimeByCode(formValues);
    }

    if (!!formValues[SpecFieldNames.DISCHARGE_TIME]) {
      selectedItem.unloadTime = await this.getUnloadTime(formValues);
    }

    if (!!formValues[SpecFieldNames.SLUMP]) {
      selectedItem.slump = await this.getSlump(formValues);
    }

    if (!!formValues[SpecFieldNames.SLUMP_PRODUCT]) {
      selectedItem.slump = await this.getSlumpProduct(formValues);
    }

    const command = experienceCode === ORDER_EXPERIENCES.COMMAND_UNIVERSAL_LINK;
    if (!!formValues[SpecFieldNames.SPACING]) {
      selectedItem.spacing = command ? await this.getSpacingById(formValues) : await this.getSpacing(formValues);
    }

    if (!!formValues[SpecFieldNames.LOAD_SIZE]) {
      selectedItem.loadSize = await this.getLoadSize(formValues);
    }

    if (!!formValues[SpecFieldNames.PUMP]) {
      selectedItem.pumpCapacity = await this.getPumpCapacity(formValues);
    }

    if (
      !!formValues[SpecFieldNames.PUMP] &&
      countryCode === COUNTRY_CODE.MEXICO &&
      experienceCode === ORDER_EXPERIENCES.RMX_UNIVERSAL_LINK
    ) {
      selectedItem.pumpService = await this.getPumpServices(formValues);
    }

    if (!!formValues[SpecFieldNames.COLOR_INFO]) {
      selectedItem.colorInfo = formValues[SpecFieldNames.COLOR_INFO];
    }

    if (commandDco) {
      selectedItem.schedule = await this.getSlotDateTime();
    } else {
      selectedItem.schedule = [];
    }

    return selectedItem;
  }

  async createULOrderReqHeaders(orderDraft: boolean, isModify?: boolean) {
    const recipes = await this.getRmsSpecificationRecipe();
    const country = (await this.getCountryCode()).trim();
    const experienceCode = await this.getExperienceCode();
    const experience = this.experiencesService;
    const commandExperienceAndDcoEnabled = await experience.commandDcoEnabled$().pipe(take(1)).toPromise();
    const improvedDcoEnabled = await experience.experienceDcoEnabled$().pipe(take(1)).toPromise();
    let customHeaders = {};
    let metadataAnalytics = null;
    const arkikExperienceAndDcoEnabled = await experience.experienceDcoEnabled$().pipe(take(1)).toPromise();

    if (country === COUNTRY_CODE.USA && experienceCode === ORDER_EXPERIENCES.ARKIK_UNIVERSAL_LINK) {
      customHeaders = {
        workflowCode: CommercialChannel.SINGLE_ORDER,
      };
    }

    if (experienceCode === ORDER_EXPERIENCES.RMX_UNIVERSAL_LINK) {
      const scheduleAvailability = await this.getSMultiplecheduleAvailability(recipes, isModify);
      const foreignJobsite = improvedDcoEnabled ? await this.isForeignJobsiteDCO() : await this.isForeignJobsite();
      if (foreignJobsite) {
        this.store.dispatch(new SaveSpecialScenarioActive([SpecialScenarioCode.FOREIGN_SERVICES]));
      }
      customHeaders = {
        dcoId: await this.getDigitalConfirmationTicket(),
        dcoExecutionTime: await this.getDigitalConfirmationRunTime(),
        orderDraft: orderDraft,
        bigOrder: await this.isBigOrder(),
        foreignJobsite: foreignJobsite,
        pumpService: await this.getPumpServiceHeader(recipes[0].id, country, recipes),
        modifyReason: await this.getModifyReasonCode(),
        modifyComments: await this.getModifyReasonComment(),
        waitingList: await this.getWaitingList(),
        hasPump: await this.hasPump(),
        isTapi: await this.hasMixVeyor(),
        isRawMaterialRestriction: await this.isRawMaterial(),
        hasDeliveryBlock: await this.hasDeliveryBlock(),
        vehicleCapacity: await this.getVehicleCapacity(),
        isModify: isModify,
        scheduleAvailability: scheduleAvailability,
      };

      if (country === COUNTRY_CODE.USA) {
        customHeaders = {
          ...customHeaders,
          workflowCode: CommercialChannel.SINGLE_ORDER,
        };
      }

      if (country === COUNTRY_CODE.UNITED_KINGDOM) {
        // Fetch special scenarios asynchronously
        const specialScenarios = await this.getSpecialScenarios();

        // Add NO_AVAILABILITY only if schedule availability is false
        // and the order is not marked as LARGE_ORDER
        const shouldAddNoAvailability =
          !scheduleAvailability && !specialScenarios.includes(SpecialScenarioCode.LARGE_ORDER);

        customHeaders = {
          ...customHeaders,
          workflowCode: CommercialChannel.SINGLE_ORDER,
          specialScenarios: specialScenarios.concat(
            shouldAddNoAvailability ? [SpecialScenarioCode.NO_AVAILABILITY] : [],
            foreignJobsite ? [SpecialScenarioCode.FOREIGN_SERVICES] : [],
          ),
          valueAddeds: await this.addedValuesMixReference(recipes[0].values, true),
        };

        metadataAnalytics = await this.getDcoIds();
        return {
          'Metadata-Transaction': customHeaders,
          'X-Metadata-Analytics': metadataAnalytics,
        };
      }
    } else if (experienceCode === ORDER_EXPERIENCES.SIMMA_UNIVERSAL_LINK) {
      customHeaders = {
        ...customHeaders,
        isModify: isModify,
        scheduleAvailability: await this.getScheduleAvailability(),
        dcoId: await this.getDigitalConfirmationTicket(),
        dcoExecutionTime: await this.getDigitalConfirmationRunTime(),
        orderDraft: orderDraft,
        bigOrder: await this.isBigOrder(),
        foreignJobsite: await this.isForeignJobsite(),
        pumpService: {
          installationTime: 30,
        },
        modifyReason: await this.getModifyReasonCode(),
        modifyComments: await this.getModifyReasonComment(),
      };
    } else if (commandExperienceAndDcoEnabled || arkikExperienceAndDcoEnabled) {
      customHeaders = {
        ...customHeaders,
        isModify: isModify,
        scheduleAvailability: await this.getSMultiplecheduleAvailability(recipes, isModify),
        waitingList: await this.getWaitingList(),
        flexibilityId: await this.getWaitingListDeliveryTimeFlexibilityId(),
        waitingListReasonCode: await this.getWaitingListReasonCode(),
        orderDraft: orderDraft,
        specialScenarios: await this.getSpecialScenarios(),
        isScheduleDCO: await this.getScheduleDCOMetadata(),
        isLiabilityCheck: await this.getLiabilityCheck(),
        bigOrder: await this.isBigOrder(),
      };

      if (country === COUNTRY_CODE.USA) {
        customHeaders = {
          ...customHeaders,
          workflowCode: CommercialChannel.SINGLE_ORDER,
        };
      }

      metadataAnalytics = await this.getDcoIds();
      return { 'Metadata-Transaction': customHeaders, 'X-Metadata-Analytics': metadataAnalytics };
    } else {
      customHeaders = {
        ...customHeaders,
        orderDraft: orderDraft,
      };
    }

    return { 'Metadata-Transaction': customHeaders };
  }

  private async getSpecialScenarios() {
    return this.store
      .select(getSpecialScenariosAvailable)
      .pipe(
        take(1),
        map((specialScenarios) => {
          return specialScenarios;
        }),
      )
      .toPromise();
  }
  private async getDcoIds() {
    return this.store
      .select(getDcoIds)
      .pipe(
        withLatestFrom(this.store.select(getLocationSpecificationState)),
        take(1),
        map(([dcoId, locationSpecificationState]) => {
          return {
            dcoId: dcoId,
            isRepeat: _safe(
              () => !!locationSpecificationState.locationSpecificationFormState.forms[0].values.isRepeat,
              false,
            ),
          };
        }),
      )
      .toPromise();
  }

  /**
   * Retrieves the pump service header for a given form ID and country.
   *
   * @param {number} formId - The ID of the form to retrieve the slot for.
   * @param {string} country - The country code to determine specific logic.
   * @returns {Promise<{ installationTime: number; pump?: any }>} A promise that resolves to an object containing the
   *  installation time and optionally the pump information.
   */
  private async getPumpServiceHeader(formId: number, country: string, formValues: RecipeFormValues[]) {
    const calendarState = await this.getCalendar();
    const pumpStartDateTime = await this.getStartDate(formId, formValues);
    const pumpFinishDateTime = await this.getEndDate(formId, formValues);
    const pumpCodeAssigned = await this.getPumpAssigned(formId, formValues);
    return this.store
      .select(getSlotForFormID(formId))
      .pipe(
        take(1),
        map((slot) => {
          if (country !== COUNTRY_CODE.MEXICO) {
            return {
              installationTime: 30,
            };
          }
          return {
            pumpStartDateTime,
            pumpFinishDateTime,
            pumpCodeAssigned,
            installationTime: this.getCalculatedInstallationTime(calendarState, formValues[formId].values),
          };
        }),
      )
      .toPromise();
  }

  private async getCalendar(): Promise<Calendar> {
    return this.store
      .select(getFirstCalendar)
      .pipe(
        take(1),
        map((state) => state),
      )
      .toPromise();
  }

  private async getModifyReasonCode() {
    return this.store
      .select(getModifyReasonCode)
      .pipe(
        map((reasonCode) => {
          return reasonCode ? reasonCode : null;
        }),
        take(1),
      )
      .toPromise();
  }

  private async getModifyReasonComment() {
    return this.store
      .select(getModifyReasonComment)
      .pipe(
        map((comment) => {
          return comment ? comment : '';
        }),
        take(1),
      )
      .toPromise();
  }

  private getDateTimeCommand(specificationForm: { [fieldName: string]: any }) {
    let timeString = '00:00:00';
    if (specificationForm[SpecFieldNames.TIME]) {
      timeString = moment(specificationForm[SpecFieldNames.TIME])
        .locale(this.translateService.instant('lang'))
        .format('HH:mm:ss');
    }

    const dateString = moment(specificationForm[SpecFieldNames.DATE])
      .locale(this.translateService.instant('lang'))
      .format('YYYY-MM-DD');

    const dateTimeString = `${dateString}T${timeString}`;

    return dateTimeString;
  }

  private async getCountryCode() {
    return this.store
      .select(getLegalEntity)
      .pipe(
        take(1),
        map((customer) => customer.countryCode),
      )
      .toPromise();
  }

  private async getCustomer() {
    return this.store
      .select(getLegalEntity)
      .pipe(
        take(1),
        map((customer) => {
          return {
            customerId: _safe(() => customer.legalEntityId),
            customerCode: _safe(() => customer.legalEntityTypeCode),
          };
        }),
      )
      .toPromise();
  }

  private async getJobsite() {
    return this.store
      .select(getShimpmentLocations)
      .pipe(
        take(1),
        map((location) => {
          return {
            jobsiteId: _safe(() => location.jobsite.shipmentLocationId),
            jobsiteCode: _safe(() => location.jobsite.shipmentLocationCode),
            jobsiteDesc: _safe(() => location.jobsite.shipmentLocationDesc),
          };
        }),
      )
      .toPromise();
  }

  private async getPointOfDelivery(country: COUNTRY_CODE) {
    return this.store
      .select(getShimpmentLocationsAndExperience)
      .pipe(
        take(1),
        map((location) => {
          const experienceCode = _safe(() => location.experience.activeExperienceCode);
          const valueAddedFeature = _safe(() =>
            location.experience.features.find((feature) => feature.key === VALUE_ADDED_SPECIALIZED_FEATURE),
          );
          const valueAddedFeatureIsEnabled = valueAddedFeature != undefined && valueAddedFeature.enabled;
          if (
            !!country &&
            !!experienceCode &&
            VL_COUNTRIES.includes(country) &&
            ((VL_EXPERIENCES.includes(ORDER_EXPERIENCES.COMMAND_UNIVERSAL_LINK) && valueAddedFeatureIsEnabled) ||
              VL_EXPERIENCES.includes(ORDER_EXPERIENCES.ARKIK_UNIVERSAL_LINK))
          ) {
            if (
              !!location &&
              !!location.jobsite &&
              !!location.pointOfDelivery &&
              location.jobsite.shipmentLocationCode === location.pointOfDelivery.shipmentLocationCode
            ) {
              return { pointOfDeliveryType: {} };
            }
          }
          return {
            pointOfDeliveryId: _safe(() => location.pointOfDelivery.shipmentLocationId),
            pointOfDeliveryCode: _safe(() => location.pointOfDelivery.shipmentLocationCode),
            pointOfDeliveryDesc: _safe(() => location.pointOfDelivery.shipmentLocationDesc),
            pointOfDeliveryType: {
              pointOfDeliveryTypeId: _safe(() => location.pointOfDelivery.shipmentLocationType.shipmentLocationTypeId),
              pointOfDeliveryTypeCode: _safe(
                () => location.pointOfDelivery.shipmentLocationType.shipmentLocationTypeCode,
              ),
            },
          };
        }),
      )
      .toPromise();
  }

  private async getContact(isModify: boolean) {
    return this.store
      .select(getLocationSelectedStates)
      .pipe(
        take(1),
        map((state) => {
          const recievingPersonSyncFeature = this.featureToggleService.isFeatureEnabled(
            Features.RECEIVING_PERSON_SYNCHRONIZATION,
          );
          // the receiving person field has the contact name forced on the receiving person list
          const hasReceivingPersonSync = state.recievingPerson.name === `${state.recievingPerson.contactId}`;
          if (!state.recievingPerson) {
            return null;
          }

          if (isModify && recievingPersonSyncFeature && hasReceivingPersonSync) {
            return {
              contactName: state.recievingPerson.name,
            };
          }

          return {
            contactId: state.recievingPerson.contactId,
            contactCode: state.recievingPerson.contactCode,
            contactName: state.recievingPerson.name,
          };
        }),
      )
      .toPromise();
  }

  private async getPurchaseOrder() {
    return this.store
      .select(getPurchaseOrderSelected)
      .pipe(
        take(1),
        map((purchaseOrder) => {
          return purchaseOrder;
        }),
      )
      .toPromise();
  }

  private async getInstrutions() {
    return this.store
      .select(getSpecialInstructionsSelected)
      .pipe(
        take(1),
        map((instructions) => instructions),
      )
      .toPromise();
  }

  private async getShippingCondition() {
    return this.store
      .select(getSelectedShippingConditions)
      .pipe(
        take(1),
        map((shippingCondition) => {
          return _safe(() => ({
            shippingConditionId: shippingCondition.shippingConditionId,
            shippingConditionCode: shippingCondition.shippingConditionCode,
          }));
        }),
      )
      .toPromise();
  }

  private async getProductType() {
    return this.store
      .select(getBusinessSelectedStates)
      .pipe(
        take(1),
        map((state) => {
          return _safe(() => ({
            productTypeId: state.businessLine.productTypeId,
            productTypeCode: state.businessLine.productTypeCode,
          }));
        }),
      )
      .toPromise();
  }

  /**
   * Provides [productCodes] api field
   */
  private async products(formValues: { [fieldName: string]: any }) {
    const country = await this.getCountryCode();
    const commProduct =
      country === COUNTRY_CODE.UNITED_KINGDOM || country === COUNTRY_CODE.FRANCE
        ? await this.commProductMixReference(formValues)
        : await this.getProduct(formValues);
    return commProduct;
  }

  private async getProduct(formValues: { [fieldName: string]: any }) {
    const productCode = _safe(() => formValues[SpecFieldNames.PRODUCT_SELECTION]);
    return this.store
      .select(getOpenPlatformProducts)
      .pipe(
        map((availableProducts) =>
          _safe(() => availableProducts.find((product) => product.productCode.replace(/^0+/, '') === productCode)),
        ),
        take(1),
      )
      .toPromise();
  }

  private async getExtraProducts(formValues: { [fieldName: string]: any }) {
    const extraProductCodes: BuildingMaterial[] = _safe(() => formValues[SpecFieldNames.EXTRA_PRODUCTS], []);
    const contractProduct: string = _safe(() => formValues[SpecFieldNames.CONTRACT_SELECT]);

    return this.store
      .select(getOpenPlatformExtraProducts)
      .pipe(
        map((availableProducts) =>
          availableProducts.filter((product) =>
            product.contracts
              .map((contract) => contract.contractCode.replace(/^0+/, ''))
              .includes(contractProduct.replace(/^0+/, '')),
          ),
        ),
        map((availableProducts) => {
          return extraProductCodes
            .map((product) => {
              const foundProduct = availableProducts.find((available) =>
                available.productCode.includes(product.material),
              );
              return {
                foundProduct,
                quantity: product.quantity,
              };
            })
            .filter((item) => item.foundProduct !== undefined);
        }),
        take(1),
      )
      .toPromise();
  }

  /**
   * Provides [commProduct] api field
   */
  private async commProductMixReference(formValues: { [fieldName: string]: any }) {
    const country = await this.getCountryCode();
    const productCode = _safe(() => formValues[SpecFieldNames.PRODUCT_SELECTION]);
    if (country === COUNTRY_CODE.UNITED_KINGDOM) {
      const productDesc = _safe(() => formValues[SpecFieldNames.PRODUCT_DESCRIPTION]);
      return this.store
        .select(getOpenPlatformProducts)
        .pipe(
          map((products) =>
            products.find(
              (available) =>
                makeReference(available) === productCode && (!productDesc || available.productDesc === productDesc),
            ),
          ),
          take(1),
        )
        .toPromise();
    } else {
      return this.store
        .select(getOpenPlatformProducts)
        .pipe(
          map((products) => products.find((available) => makeReference(available) === productCode)),
          take(1),
        )
        .toPromise();
    }
  }

  private async getContract(country: COUNTRY_CODE) {
    return this.store
      .select(getRmsRecipeSpecifications)
      .pipe(
        take(1),
        map((form) => _safe(() => form[0].values)),
        switchMap((recipe) => {
          const contractCode = _safe(() => recipe[SpecFieldNames.CONTRACT_SELECT]);
          const productCode = _safe(() => recipe[SpecFieldNames.PRODUCT_SELECTION]);
          return this.store.select(getOpenPlatformContracts).pipe(
            map((availableContracts) => availableContracts[productCode]),
            map((contracts) => {
              const contractSelected = _safe(() =>
                contracts.find(
                  (contract) => contract.contractCode.replace(/^0+/, '') === contractCode.replace(/^0+/, ''),
                ),
              );
              if (country.trim() === COUNTRY_CODE.ISRAEL && contractSelected) {
                return {
                  contractCode: contractSelected.contractCode,
                  contractId: contractSelected.contractId,
                };
              } else if (country.trim() === COUNTRY_CODE.FRANCE) {
                return {
                  contractCode: contracts[0].contractCode,
                  contractId: contracts[0].contractId,
                };
              } else if (contractSelected) {
                return {
                  contractCode: contractSelected.contractCode,
                };
              } else {
                return {};
              }
            }),
            take(1),
          );
        }),
      )
      .toPromise();
  }

  private async getSpacing(formValues: { [fieldName: string]: any }, spacingCodeParam?: string) {
    return this.store
      .select(getLocalSpacings)
      .pipe(
        map((spacings) => {
          const spacingCode = _safe(() => formValues[SpecFieldNames.SPACING]) || spacingCodeParam;
          const selectedSpacing = _safe(() =>
            spacings.find((item) => item.spacingCode === spacingCode || item.spacingId === spacingCode),
          );
          return {
            spacingId: selectedSpacing.spacingId,
            spacingCode: selectedSpacing.spacingCode,
            spacingMinutes: selectedSpacing['spacingMinutes'],
          };
        }),
        take(1),
      )
      .toPromise();
  }

  private async getSpacingById(formValues: { [fieldName: string]: any }) {
    return this.store
      .select(getLocalSpacings)
      .pipe(
        map((spacings) => {
          const spacingId = _safe(() => formValues[SpecFieldNames.SPACING]);
          // Creation still uses spacingCode values
          const selectedSpacing =
            _safe(() => spacings.find((item) => item.spacingId === spacingId)) ||
            _safe(() => spacings.find((item) => item.spacingCode === spacingId));
          return {
            spacingId: selectedSpacing.spacingId,
            spacingCode: selectedSpacing.spacingCode,
            spacingMinutes: selectedSpacing['spacingMinutes'],
          };
        }),
        take(1),
      )
      .toPromise();
  }

  private async getLoadSize(formValues: { [fieldName: string]: any }) {
    return this.store
      .select(getLoadSizes)
      .pipe(
        map((loadSizes) => {
          const loadSizeCode = _safe(() => formValues[SpecFieldNames.LOAD_SIZE]);
          const selectedLoadSize = _safe(() => loadSizes.find((item) => item.loadSizeCode === loadSizeCode));
          return {
            loadSizeCode: selectedLoadSize.loadSizeCode,
            loadSizeDesc: selectedLoadSize.loadSizeDesc,
            loadSizeId: selectedLoadSize.loadSizeId,
          };
        }),
        take(1),
      )
      .toPromise();
  }

  private async getPumpCapacity(formValues: { [fieldName: string]: any }) {
    return this.store
      .select(getPumpCapacities)
      .pipe(
        map((pumpCapacities) => {
          const pumpCode = _safe(() => formValues[SpecFieldNames.PUMP]);
          const selectedPumpCapacity = _safe(() => pumpCapacities.find((item) => item.pumpCapacityCode === pumpCode));
          return {
            pumpCapacityCode: _safe(() => selectedPumpCapacity.pumpCapacityCode),
            pumpCapacityDesc: _safe(() => selectedPumpCapacity.pumpCapacityDesc),
            pumpCapacityId: _safe(() => selectedPumpCapacity.pumpCapacityId),
          };
        }),
        take(1),
      )
      .toPromise();
  }

  /**
   * Get pump services for the RMX UL 5.2 experience
   * @param formValues
   * @returns pumpServices { pipes, hoses, installationTime, cleaningOnSite, pumpLength, pumpSize }
   */
  private async getPumpServices(formValues: { [fieldName: string]: any }) {
    return this.store
      .select(getFirstCalendar)
      .pipe(
        map((calendarState) => {
          const pumpCode = _safe(() => formValues[SpecFieldNames.PUMP]);
          const pumpLength = _safe(() => formValues[SpecFieldNames.PUMP_LENGTH]);
          const hoses = _safe(() => formValues[SpecFieldNames.HOSES]);
          const cleaningOnSite =
            typeof formValues[SpecFieldNames.CLEAN_UP] === 'string'
              ? formValues[SpecFieldNames.CLEAN_UP] === 'true'
              : formValues[SpecFieldNames.CLEAN_UP];
          const pumpId = calendarState.pump['pumpId'];
          const pipes = this.getPipes(calendarState, formValues);
          const pump = {
            pipes: pipes,
            hoses: hoses,
            installationTime: this.getCalculatedInstallationTime(calendarState, formValues),
            cleaningOnSite: cleaningOnSite,
            pumpId: pumpId,
            maxPumpType: pumpLength,
            pumpSize: {
              pumpSizeCode: pumpCode,
            },
          } as any;
          if (calendarState.pump.flagLinearMeters) {
            pump['linearMetersPumping'] = formValues[SpecFieldNames.LINEAR_METERS];
          }
          return pump;
        }),
        take(1),
      )
      .toPromise();
  }

  /**
   * Calculates the number of pipes based on the provided calendar configuration
   * and form values. If the calendar's pump does not use linear meters, it directly
   * returns the pipe quantity from the form values. Otherwise, it calculates the
   * number of pipes required by dividing the linear meters by 3 and rounding up
   * to the nearest whole number.
   *
   * @param calendar - The calendar object containing pump configuration details.
   * @param formValues - An object containing form field values, where keys are
   *                     field names and values are their corresponding inputs.
   * @returns The calculated number of pipes.
   */
  private getPipes(calendar: Calendar, formValues: { [fieldName: string]: any }) {
    if (!calendar.pump.flagLinearMeters) {
      return formValues[SpecFieldNames.PIPE_QUANTITY];
    }
    // has linear meters
    return Math.ceil((formValues[SpecFieldNames.LINEAR_METERS] || 0) / 3);
  }

  private async getApplication(formValues: { [fieldName: string]: any }) {
    return this.store
      .select(getApplicationsAndCountryLang)
      .pipe(
        map((state) => {
          const applicationCode = _safe(() => formValues[SpecFieldNames.APPLICATION]);
          let selectedApplication = _safe(() =>
            state.applications.find((item) => item.applicationCode.toLowerCase() === applicationCode.toLowerCase()),
          );

          if (state.country === COUNTRY_CODE.UNITED_KINGDOM) {
            if (selectedApplication === undefined) {
              if (applicationCode.toLowerCase() === 'cubes') {
                selectedApplication = state.applications.find((item) => item.applicationCode.toLowerCase() === 'cube');
              } else {
                selectedApplication = state.applications.find((item) => item.applicationCode.toLowerCase() === 'base');
              }
            } else if (selectedApplication.applicationCode.toLowerCase() === 'cubes') {
              selectedApplication.applicationCode = 'CUBE';
            }
          }

          const applicationToSend =
            state.country === COUNTRY_CODE.FRANCE && state.language === 'fr_FR'
              ? selectedApplication.applicationDesc
              : selectedApplication.applicationCode;
          return {
            applicationId: selectedApplication.applicationId,
            applicationCode: applicationToSend,
          };
        }),
        take(1),
      )
      .toPromise();
  }

  private async getUnloadTime(formValues: { [fieldName: string]: any }) {
    return this.store
      .select(getUnloadTimesWithLocalData)
      .pipe(
        map((unloadTimes) => {
          const unloadTimeCode = _safe(() => formValues[SpecFieldNames.DISCHARGE_TIME]);
          const selectedUnloadTime = _safe(() => unloadTimes.find((item) => item.unloadTimeCode === unloadTimeCode));
          return {
            unloadTimeId: selectedUnloadTime.unloadTimeId,
            unloadTimeCode: selectedUnloadTime.unloadTimeCode,
          };
        }),
        take(1),
      )
      .toPromise();
  }

  /**
   * Retrieves the `UnloadTime` object based on the unload time code from form values.
   * @returns Promise<UnloadTime>
   */
  private async getUnloadTimeByCode(formValues: { [fieldName: string]: string }): Promise<UnloadTime> {
    return this.store
      .select(getUnloadTimesWithLocalData)
      .pipe(
        map((unloadTimes) => {
          const unloadTimeCode = _safe(() => formValues[SpecFieldNames.UNLOAD_TIME]);
          const selectedUnloadTime = unloadTimes.find((item) => item.unloadTimeCode === unloadTimeCode);
          return {
            unloadTimeId: selectedUnloadTime.unloadTimeId,
            unloadTimeCode: selectedUnloadTime.unloadTimeCode,
          };
        }),
        take(1),
      )
      .toPromise();
  }

  private async getUnloadType(formValues: { [fieldName: string]: any }, experienceCode: ORDER_EXPERIENCES) {
    return this.store
      .select(getUnloadTypesAndCountryLang)
      .pipe(
        map((state) => {
          const unloadTypeCode = _safe(() => formValues[SpecFieldNames.UNLOAD_TYPE]);
          let selectedunloadType = _safe(() =>
            state.unloadTypes.find((item) => item.unloadTypeCode === unloadTypeCode),
          );

          if (selectedunloadType === undefined && state.country === COUNTRY_CODE.UNITED_KINGDOM) {
            selectedunloadType = state.unloadTypes.find((item) => item.unloadTypeCode.toLowerCase() === '30_MIN');
            if (selectedunloadType === undefined) {
              const defaultUnloadTypeUK = {
                unloadTypeCode: '30_MIN',
                unloadTypeDesc: '30 min',
                unloadTypeId: 68,
              } as ImUnloadType;
              selectedunloadType = defaultUnloadTypeUK;
            }
          }

          if (experienceCode === ORDER_EXPERIENCES.ARKIK_UNIVERSAL_LINK) {
            return {
              unloadTypeId: 11,
              unloadTypeCode: 'DIRECT_FLOW',
            };
          }

          const unloadTypeToSend =
            state.country === COUNTRY_CODE.FRANCE && state.language === 'fr_FR'
              ? selectedunloadType?.unloadTypeDesc.toUpperCase()
              : selectedunloadType?.unloadTypeCode;
          return {
            unloadTypeId: selectedunloadType?.unloadTypeId,
            unloadTypeCode: unloadTypeToSend,
            unloadTypeDesc: selectedunloadType?.unloadTypeDesc,
          };
        }),
        take(1),
      )
      .toPromise();
  }

  private async getPlant() {
    const availableSlot = await this.getAvailableSlotDate();
    return this.store
      .select(getRmsRecipeSpecifications)
      .pipe(
        take(1),
        map((form) => _safe(() => form[0].values)),
        switchMap((recipe) => {
          const dateTime = this.getDateTime(availableSlot, recipe);
          return this.store.select(getOpenPlatformSlots).pipe(
            map(async (slots) => {
              const plant = _safe(
                () =>
                  slots[0].response.slots[0].concreteProposals.find(
                    (proposal) => proposal.deliveryDate === dateTime.date && proposal.arriveAtText === dateTime.time,
                  ).plant,
              );
              return plant || (await this.defaultPlant());
            }),
            take(1),
          );
        }),
      )
      .toPromise();
  }
  private async getPlantDCO() {
    const availableSlot = await this.getAvailableSlotDate();
    return this.store
      .select(getRmsRecipeSpecifications)
      .pipe(
        take(1),
        map((form) => ({ recipe: _safe(() => form[0].values), formId: _safe(() => form[0].id) })),
        switchMap(({ recipe, formId }) => {
          const dateTime = this.getDateTime(availableSlot, recipe);
          return this.store.select(getSlotForFormID(formId)).pipe(
            map(async (slot) => {
              let plant: string;
              plant = _safe(
                () =>
                  slot.response.slots[0].concreteProposals.find(
                    (proposal) => proposal.deliveryDate === dateTime.date && proposal.arriveAtText === dateTime.time,
                  ).plant,
              );
              if (plant == undefined && !!slot && !!slot.timeSlots) {
                // date format 'YYYY-MM-DD'
                // time format 'HH:mm'
                const timeSlotFound = slot.timeSlots.find((timeSlot) => {
                  const plantOpeningDate = timeSlot.dateMoment.clone().format('YYYY-MM-DD');
                  if (plantOpeningDate === dateTime.date) {
                    return true;
                  }
                  return false;
                });
                if (timeSlotFound != undefined) {
                  plant = timeSlotFound.plantCode;
                }
              }
              const isBigOrder = await this.isBigOrder();
              const scheduleAvailability = await this.getDCOAvailability(formId);
              const ForeignJobsites = await this.isForeignJobsiteDCO();
              const hasSpecialScenario = await this.hasSpecialScenario();
              if (!!isBigOrder || scheduleAvailability === false || !!ForeignJobsites || hasSpecialScenario) {
                const plantCode = await this.getPlantFromOrderDetail();
                const experienceCode = await this.getExperienceCode();
                if (
                  plantCode &&
                  experienceCode === ORDER_EXPERIENCES.ARKIK_UNIVERSAL_LINK &&
                  this.featureToggleService.isFeatureEnabled(Features.MODIFIED_FIELDS_ARKIK)
                ) {
                  return plantCode;
                }
                return await this.defaultPlant();
              }
              return plant;
            }),
            take(1),
          );
        }),
      )
      .toPromise();
  }

  private async getVehicleCapacity() {
    const defaultVehicleCapacity = await this.defaultVehicleCapacity();
    return this.store
      .select(getSelectedSlot)
      .pipe(
        take(1),
        map((state) => {
          if (_safe(() => state['vehicleCapacity'])) {
            return state['vehicleCapacity'];
          } else {
            return defaultVehicleCapacity;
          }
        }),
      )
      .toPromise();
  }

  /**
   * Gets distance and time parameters from the selected slot
   */
  private async getSlotDateTime() {
    return this.store
      .select(getSlotAndTravelTime)
      .pipe(
        map((state) => {
          if (state.orderLength === 1) {
            return [
              {
                toJobTravelTime: _safe(() => (state.slot.travelTimeToPlant as any).length)
                  ? _safe(() => state.slot.travelTimeToPlant[0])
                  : _safe(() => state.slot.travelTimeToPlant),
                toPlantTravelTime: _safe(() => (state.slot.travelTimeFromPlant as any).length)
                  ? _safe(() => state.slot.travelTimeFromPlant[0])
                  : _safe(() => state.slot.travelTimeFromPlant),
                distance: _safe(() => state.slot.travelDistance),
              },
            ];
          } else {
            return [];
          }
        }),
        take(1),
      )
      .toPromise();
  }

  private async getAvailableSlotDate() {
    return this.store
      .select(getOpenPlatformSlots)
      .pipe(
        take(1),
        map((response) => {
          return response && response[0] && response[0].availableDate;
        }),
      )
      .toPromise();
  }

  private async getSlotWaitingListDefaulted() {
    const waitingListDefaultedPromise = this.store
      .select(getActiveExperienceCode)
      .pipe(
        take(1),
        withLatestFrom(this.store.select(getOpenPlatformSlots)),
        withLatestFrom(this.store.select(getSlotWaitingListDefaulted)),
        map(([[experienceCode, slots], waitingListDefaultedValue]) => {
          if (experienceCode === ORDER_EXPERIENCES.COMMAND_UNIVERSAL_LINK) {
            // get waiting list from open platform state
            return slots.every((slot) => slot.waitingListDefaulted === undefined || slot.waitingListDefaulted === true);
          }
          // legacy state
          return waitingListDefaultedValue;
        }),
      )
      .toPromise();
    return waitingListDefaultedPromise;
  }

  private getDateTime(availableSlot, recipe) {
    if (availableSlot === undefined || availableSlot === null) {
      return {
        date: moment(_safe(() => recipe[SpecFieldNames.SCHEDULE_MOMENT])).format('YYYY-MM-DD'),
        time: moment(_safe(() => recipe[SpecFieldNames.SCHEDULE_MOMENT])).format('HH:mm'),
      };
    } else {
      return {
        date: availableSlot.format('YYYY-MM-DD'),
        time: availableSlot.format('HH:mm'),
      };
    }
  }

  /**
   * Get the value added from the selection on the form (UI)
   * and prepend if the country has mandatory value added
   * @param formIndex
   * @param countryCode
   * @returns ValueAddedOP[]
   */
  private async addedValues(formValues: { [fieldName: string]: any }, countryCodeParameter) {
    return this.store
      .select(addedValuesAndProducts)
      .pipe(
        switchMap(async ({ addedValuesState, availableProducts }) => {
          const families: ValueAddedOP[] = [];
          const productCode = _safe(() => formValues[SpecFieldNames.PRODUCT_SELECTION]);
          const keyName = `${formValues[SpecFieldNames.CONTRACT_SELECT]}_${formValues[SpecFieldNames.ID]}`;
          const product = availableProducts.find((products) => products.productCode.replace(/^0+/, '') === productCode);
          const availableAddedValue = addedValuesState.availableAddedValues[keyName];
          const addedValue = availableAddedValue && availableAddedValue[0];
          const baseName = 'addedProduct';
          const addedValueSelectedByUser = formValues[generateAddedValueFormName(0)];

          for (const name of Object.keys(formValues)) {
            if (name.substr(0, baseName.length) === baseName && _safe(() => formValues['addedProduct0'])) {
              const valueAddedId: string = _safe(() => formValues[name]);
              const addedValueSelected = addedValue.addedValues.find((item) => item.valueAddedCode === valueAddedId);
              const value = {
                valueAddedCode: addedValueSelected && addedValueSelected.valueAddedCode,
              };
              if (!!addedValueSelected && value) {
                families.push(value);
              }
            }
          }

          return { families, product, countryCode: countryCodeParameter, addedValueSelected: addedValueSelectedByUser };
        }),
        switchMap(async ({ families, product, countryCode, addedValueSelected }) => {
          let preprendedVaps: ValueAddedOP[] = [];
          if (product['productXType'] === '0' && !!addedValueSelected && COUNTRY_CODE.MEXICO === countryCode) {
            const prependValueAddedCodes = prependedValueAdded(product).map((valueAddedCode) => {
              return {
                valueAddedCode,
              };
            });
            preprendedVaps = prependValueAddedCodes;
          }
          return [...preprendedVaps, ...families].filter((_) => !!_);
        }),
        take(1),
      )
      .toPromise();
  }

  /**
   * Provides addedValues used by Mix reference
   */
  private async addedValuesMixReference(formValues: { [fieldName: string]: any }, isCustomHeader?: boolean) {
    return this.store
      .select(addedValuesAndProducts)
      .pipe(
        switchMap(async (availableProducts) => {
          const productCode = formValues[SpecFieldNames.PRODUCT_SELECTION];
          const productDesc = formValues[SpecFieldNames.PRODUCT_DESCRIPTION];
          const product = availableProducts.availableProducts.find(
            (available) =>
              makeReference(available) === productCode && (!productDesc || available.productDesc === productDesc),
          );
          return { product };
        }),
        switchMap(async ({ product }) => {
          const families: ValueAddedOP[] = [];
          product.contracts[0].valueAddeds.forEach((item) => {
            const value = {
              valueAddedCode: item.valueAddedCode,
            };
            if (isCustomHeader) {
              value['valueAddedType'] = item.valueAddedType;
            }

            families.push(value);
          });
          return { families, product };
        }),
        map(({ families, product }) => {
          const preprendedVaps: ValueAddedOP[] = [];
          return [...preprendedVaps, ...families].filter((_) => !!_);
        }),
        take(1),
      )
      .toPromise();
  }

  private async technicalProduct(formValues: { [fieldName: string]: any }) {
    const country = await this.getCountryCode();
    if (country === COUNTRY_CODE.UNITED_KINGDOM) {
      return await this.lastTechnicalProductMixReference(formValues);
    } else if (country === COUNTRY_CODE.MEXICO) {
      return await this.getTechnicalProduct(formValues);
    } else {
      return await this.lastTechnicalProduct(formValues);
    }
  }

  private async getValueAddeds(formValues: { [fieldName: string]: any }) {
    const country = await this.getCountryCode();
    if (country === COUNTRY_CODE.UNITED_KINGDOM) {
      return await this.addedValuesMixReference(formValues);
    } else {
      return await this.addedValues(formValues, country);
    }
  }

  private async lastTechnicalProduct(formValues: { [fieldName: string]: any }) {
    const country = await this.getCountryCode();
    const keyName = `${_safe(() => formValues[SpecFieldNames.CONTRACT_SELECT])}_${_safe(
      () => formValues[SpecFieldNames.ID],
    )}`;
    return this.store
      .select(getOpenPlatformProductAddedValues(keyName))
      .pipe(
        map((availables) => availables.filter((entry) => !(entry.loading === true || entry.addedValues === undefined))),
        map((availables) =>
          availables.filter((available) => {
            if (available.addedValues === null && available.technicalProduct === null) {
              return false;
            } else {
              return true;
            }
          }),
        ),
        map((availables) => availables[availables.length - 1]),
        map((last) => last.technicalProduct),
        map((technicals) => {
          const unloadTypeValue = _safe(() => formValues[SpecFieldNames.UNLOAD_TYPE]);
          const hasPump = isPump(unloadTypeValue);
          const slumpValue = formValues[SpecFieldNames.SLUMP];
          const pumpabilityValue = formValues[SpecFieldNames.PUMPABILITY];
          if (country === COUNTRY_CODE.USA) {
            return technicals && technicals[0];
          }
          if (country === COUNTRY_CODE.FRANCE) {
            if (!!pumpabilityValue) {
              return (
                technicals &&
                technicals.find(
                  (technical) =>
                    !!technical.isPumpable &&
                    Number(technical['pumpable']) === pumpabilityValue &&
                    technical['slump']['slumpCode'] === slumpValue,
                )
              );
            } else {
              return (
                technicals &&
                technicals.find(
                  (technical) =>
                    !technical.isPumpable &&
                    technical['pumpable'] === '0' &&
                    technical['slump']['slumpCode'] === slumpValue,
                )
              );
            }
          }

          // If the user selects Pump, needs a pumpable technical product.
          if (!!hasPump) {
            return technicals && technicals.find((technical) => technical.isPumpable === hasPump);
          }
          // If the user does not selects Pump,
          // Select the first technical product not pumpable as default.
          return technicals && technicals.find((technical) => technical.isPumpable === false);
        }),
        take(1),
      )
      .toPromise();
  }

  /**
   * Retrieves the technical product based on the provided form values.
   *
   * This method first attempts to get the technical product using the `technicalProduct` method.
   * If a technical product is found, it is returned. Otherwise, it selects the appropriate technical product
   * from the available products in the store based on the form values.
   *
   * @param formValues - The form values used to determine the technical product.
   * @returns A promise that resolves to the selected technical product.
   */
  private async getTechnicalProduct(formValues): Promise<TechnicalProductOP> {
    const technicalProduct = await this.lastTechnicalProduct(formValues);
    return this.store
      .select(getOpenPlatformProducts)
      .pipe(
        map((availables) =>
          availables.find((available) => makeReference(available) === formValues[SpecFieldNames.PRODUCT_SELECTION]),
        ),
        map((productSelected) => productSelected.technicalProducts),
        map((technicals) => {
          if (!!technicalProduct && !!technicalProduct.technicalProductCode) {
            return technicalProduct;
          }
          const unloadTypeValue = formValues[SpecFieldNames.UNLOAD_TYPE];
          const hasPump = isPump(unloadTypeValue);
          // If the user selects Pump, needs a pumpable technical product.
          if (!!hasPump) {
            return _safe(() => technicals && technicals.find((technical) => technical.isPumpable === hasPump));
          }
          // If the user does not selects Pump,
          // Select the first technical product not pumpable as default.
          return _safe(() => technicals && technicals.find((technical) => technical.isPumpable === false));
        }),
        take(1),
      )
      .toPromise();
  }

  /**
   * Provides lastTechnicalProduct used by mix reference
   */
  private async lastTechnicalProductMixReference(formValues: { [fieldName: string]: any }) {
    const productCode = _safe(() => formValues[SpecFieldNames.PRODUCT_SELECTION]);
    const productDesc = _safe(() => formValues[SpecFieldNames.PRODUCT_DESCRIPTION]);
    return this.store
      .select(getOpenPlatformProducts)
      .pipe(
        filter((availables) => !!availables.length),

        map((availables) =>
          availables.find(
            (available) =>
              makeReference(available) === productCode && (!productDesc || available.productDesc === productDesc),
          ),
        ),
        map((productSelected) => productSelected.technicalProducts[0]),
        take(1),
      )
      .toPromise();
  }

  private async getDeliveryDateHeader() {
    const availableSlot = await this.getAvailableSlotDate();
    return this.store
      .select(getRmsRecipeSpecifications)
      .pipe(
        map((form) => _safe(() => form[0].values)),
        map((recipe) => {
          const dateTimeObj = this.getDateTime(availableSlot, recipe);
          return `${dateTimeObj.date} ${dateTimeObj.time}`;
        }),
        take(1),
      )
      .toPromise();
  }

  private async getDeliveryDateByItem(formValues: { [fieldName: string]: any }) {
    const availableSlot = await this.getAvailableSlotDate();
    const scheduleMoment = formValues[SpecFieldNames.SCHEDULE_MOMENT];
    if (_safe(() => _safe(() => scheduleMoment)) !== undefined) {
      return moment(_safe(() => _safe(() => scheduleMoment))).format('YYYY-MM-DD HH:mm');
    } else {
      const reqDate = this.getDateTime(availableSlot, formValues);
      return `${reqDate.date} ${reqDate.time}`;
    }
  }

  private async getRequestedDateByitem(formValues: { [fieldName: string]: any }) {
    const availableSlot = await this.getAvailableSlotDate();
    const waitingList = formValues[SpecFieldNames.WAITING_LIST_MOMENT];
    const scheduleMoment = formValues[SpecFieldNames.SCHEDULE_MOMENT];
    if (_safe(() => waitingList) !== undefined) {
      return moment(_safe(() => waitingList)).format('YYYY-MM-DD HH:mm');
    } else if (_safe(() => _safe(() => scheduleMoment)) !== undefined) {
      return moment(_safe(() => _safe(() => scheduleMoment))).format('YYYY-MM-DD HH:mm');
    } else {
      const reqDate = this.getDateTime(availableSlot, formValues);
      return `${reqDate.date} ${reqDate.time}`;
    }
  }

  private async getWaitingList() {
    const dcoFeatureEnabled = await this.experiencesService.experienceDcoEnabled$().pipe(take(1)).toPromise();
    if (dcoFeatureEnabled) {
      const checkWaitingList$ = this.store.select(getDcoEntitiesAsArray).pipe(
        map((slots) => {
          const waitingListItemIndex = slots.findIndex((slot) => slot.isWaitingList === true);
          if (waitingListItemIndex > -1) {
            return true;
          }
          return false;
        }),
        take(1),
      );
      return checkWaitingList$.toPromise();
    }
    const waitingListDefaulted = await this.getSlotWaitingListDefaulted();
    return this.store
      .select(getRmsRecipeSpecifications)
      .pipe(
        map((recipe) => {
          const waitingList = recipe.find(
            (form) => form.values[SpecFieldNames.WAITING_LIST_MOMENT] !== undefined && !waitingListDefaulted,
          );
          if (waitingList) {
            return true;
          } else {
            return false;
          }
        }),
        take(1),
      )
      .toPromise();
  }

  private async getWaitingListDeliveryTimeFlexibilityId(): Promise<number> {
    return this.store
      .select(getDcoEntitiesAsArray)
      .pipe(
        map((slots) => slots.find((slot) => slot.isWaitingList)?.waitingListDeliveryTimeFlexibility?.featureId),
        take(1),
      )
      .toPromise();
  }

  private async getWaitingListReasonCode(): Promise<
    | SpecialScenarioCode.VOLUMENT_QUOTE
    | SpecialScenarioCode.OUT_SERVICE_HOURS
    | SpecialScenarioCode.NO_AVAILABILITY
    | undefined
  > {
    return combineLatest([this.store.select(getValidVolumeAllocation), this.store.select(getDcoEntitiesAsArray)])
      .pipe(
        take(1),
        map(([validVolumeAllocation, slots]) => {
          if (validVolumeAllocation.invalid) {
            return SpecialScenarioCode.VOLUMENT_QUOTE;
          }
          const outOfServiceTimeSelected = !isNil(slots.find((slot) => slot?.response?.outOfServiceHours));
          if (outOfServiceTimeSelected) {
            return SpecialScenarioCode.OUT_SERVICE_HOURS;
          }

          const requestDatetimeNotAvailable = !isNil(slots.find((slot) => !slot?.requestDatetimeIsAvailable));
          if (requestDatetimeNotAvailable) {
            return SpecialScenarioCode.NO_AVAILABILITY;
          }

          return undefined;
        }),
      )
      .toPromise();
  }

  /**
   * Get liabilityCheck value from selected slot by the user
   * @returns true if the order is a foreign jobsite/extended range otherwise undefined
   */
  private async getLiabilityCheck() {
    const acceptLiabilityCheck$ = this.store.select(getDcoEntitiesAsArray).pipe(
      map((slots) => {
        const liabilityCheckIndex = slots.findIndex((slot) => !!slot.liabilityCheck);
        return liabilityCheckIndex > -1 ? true : undefined;
      }),
      take(1),
    );
    return acceptLiabilityCheck$.toPromise();
  }
  private async getScheduleDCOMetadata() {
    return this.store
      .select(getSlotAndTravelTime)
      .pipe(
        map((state) => {
          return state.isScheduleDCO;
        }),
        take(1),
      )
      .toPromise();
  }
  private async getSalesArea() {
    return this.store
      .select(getSalesAreas)
      .pipe(
        map((salesAreas) => {
          return {
            salesAreaId: salesAreas[0].salesArea.salesAreaId,
          };
        }),
        take(1),
      )
      .toPromise();
  }

  private async isSpecialInvoice() {
    return this.store
      .select(getSummaryPage)
      .pipe(
        map((state) => {
          return _safe(() => !!state.specialInvoice, false);
        }),
        take(1),
      )
      .toPromise();
  }

  private async isBigOrder() {
    return this.store
      .select(getRmsRecipeSpecifications)
      .pipe(
        take(1),
        map((form) => _safe(() => form[0].values)),
        switchMap((recipe) => {
          return this.store.select(getFirstCalendar).pipe(
            map((state) => {
              const quantity = recipe[SpecFieldNames.QUANTITY];
              const bigOrderQuantity = _safe(() => state.bigOrder.quantity);
              return quantity >= bigOrderQuantity ? true : false;
            }),
            take(1),
          );
        }),
      )
      .toPromise();
  }

  private async hasSpecialScenario() {
    return this.store
      .select(getRmsRecipeSpecifications)
      .pipe(
        take(1),
        map((form) => {
          const recipe = _safe(() => form[0].values);
          return [UnloadTypePumpCodesFrance.PUMPING_SERVICE, UnloadTypeMixVeyorCodes.MIXVEYOR].includes(
            recipe.unloadType,
          );
        }),
      )
      .toPromise();
  }

  private async getDigitalConfirmationTicket() {
    return this.store
      .select(getSlotsResponse)
      .pipe(
        map((slots) => {
          return _safe(() => slots.header.prefix) !== undefined ? slots.header.prefix : '';
        }),
        take(1),
      )
      .toPromise();
  }

  private async getExperienceCode() {
    return this.store
      .select(getActiveExperienceCode)
      .pipe(
        take(1),
        map((experienceCode) => experienceCode),
      )
      .toPromise();
  }

  private async getScheduleAvailability() {
    return this.store
      .select(getOpenPlatformSlots)
      .pipe(
        take(1),
        map((slots) => {
          // check if value is false, undefined or null
          const noAvalability = slots.find(
            (slot) => slot.availableSchedule === false || slot.availableSchedule == undefined,
          );
          if (!!noAvalability === true) {
            return false;
          }
          return true;
        }),
      )
      .toPromise();
  }
  private async getDCOAvailability(formId: number) {
    return this.store
      .select(getSlotForFormID(formId))
      .pipe(
        take(1),
        map((slot) => {
          return slot.requestDatetimeIsAvailable || slot.selectedDatetimeSlot != null;
        }),
      )
      .toPromise();
  }

  private async getSMultiplecheduleAvailability(recipes?: RecipeFormValues[], isModify?: boolean) {
    const order = await this.getOrder();
    const dcoFeatureEnabled = await this.experiencesService.experienceDcoEnabled$().pipe(take(1)).toPromise();
    const cutOff = await this.store
      .select(getCutOff)
      .pipe(
        filter((response) => !!response),
        take(1),
        map((response) => response.dateAvailable),
      )
      .toPromise();
    if (dcoFeatureEnabled) {
      /*
       * Modify validation:
       * If the order don't have change return the same status of the original order
       */
      const modifyValidationChanges$ = this.store.select(getDcoStatusState).pipe(
        map((modifyStatus) => {
          // If modify status is equals to null return true, That is because is not in modify work flow.
          // If the order have changes the status is validate by special scenarios validations
          if (modifyStatus === null || modifyStatus.haveChanges === true) {
            return true;
          } else if (modifyStatus.haveChanges === false) {
            return modifyStatus.originalStatus.statusCode === OrderStatusEnum.Confirmed;
          }
          return true;
        }),
      );
      // Volume management validation
      const checkVolumeAllocation$ = this.store.select(getValidVolumeAllocation).pipe(
        map((validation) => {
          return !validation.invalid;
        }),
      );
      const colorSepcialScenarios$ = this.store.select(getDcoStatusState).pipe(
        map((modifyStatus) => {
          if (modifyStatus != null && modifyStatus.colorSpecialScenario === true) {
            return false;
          }
          return true;
        }),
      );

      // Schedule Availability validation
      const checkScheduleAvailability$ = this.store.select(getDcoEntitiesAsArray).pipe(
        map((slots) => {
          const scheduleAvailability = slots.every(
            (slot) => slot.requestDatetimeIsAvailable === true || slot.selectedDatetimeSlot != null,
          );
          return scheduleAvailability && slots.length > 0;
        }),
      );
      const observables = [
        checkVolumeAllocation$,
        checkScheduleAvailability$,
        modifyValidationChanges$,
        colorSepcialScenarios$,
      ];
      const scheduleAvailability$ = combineLatest(observables).pipe(
        take(1),
        map((validations) => {
          const validation = validations.some((valid) => valid === false);
          return !validation;
        }),
      );
      return scheduleAvailability$.toPromise();
    }
    return this.store
      .select(getOpenPlatformSlots)
      .pipe(
        take(1),
        map((slots) => {
          const scheduleAvailability = slots.every((slot) => slot.availableSchedule === true);
          const comments = recipes.some((recipe) => {
            const comment = recipe && recipe.values && recipe.values[SpecFieldNames.ADDITIONAL_INFO];
            return !!comment && comment.length > 0 ? true : false;
          });
          // When is modify flow and there are not slots loaded send as default scheduleAvailability as true
          // because if FE send false the order status will be affected
          // Is Date is before of cutofftime send true
          if (
            !!isModify &&
            order &&
            order.status.statusCode === OrderStatusEnum.Confirmed &&
            !comments &&
            (!!scheduleAvailability || slots.length === 0)
          ) {
            const isBeforeOfCuOff = recipes[0].values.scheduleMoment.isBefore(moment(cutOff), 'day');
            return isBeforeOfCuOff ? false : true;
          } else if (
            !comments &&
            slots &&
            slots.length &&
            recipes &&
            recipes.length === slots.length &&
            scheduleAvailability
          ) {
            return true;
          } else {
            return false;
          }
        }),
      )
      .toPromise();
  }

  private async getOrder() {
    return this.store
      .select(getOrderSelectedDetail)
      .pipe(
        take(1),
        map((order) => order),
      )
      .toPromise();
  }

  private async getDigitalConfirmationRunTime() {
    return this.store
      .select(getSlotsResponse)
      .pipe(
        map((slots) => {
          return _safe(() => slots.header.runtimes.total) !== undefined ? slots.header.runtimes.total : 0;
        }),
        take(1),
      )
      .toPromise();
  }

  /**
   * Get plant code depending on the calendar version
   * calendars v1: bigOrder.plantCode
   * calendars v2: shipment.plant.plantCode
   * @returns Promise<string>
   */
  private async defaultPlant() {
    return this.store
      .select(getFirstCalendar)
      .pipe(
        take(1),
        map((state) => {
          const plantCode = _safe(() => state.bigOrder.plantCode);
          if (!!plantCode) {
            return plantCode;
          }
          return _safe(() => state.shipment.plant.plantCode);
        }),
      )
      .toPromise();
  }

  private async defaultVehicleCapacity() {
    return this.store
      .select(getFirstCalendar)
      .pipe(
        take(1),
        map((state) => _safe(() => state.loadSize)),
      )
      .toPromise();
  }

  private async isForeignJobsite() {
    return this.store
      .select(getSlotsErrors)
      .pipe(
        take(1),
        map((errors) => {
          if (
            errors &&
            (_safe(() => errors.slotError.error.reasonCode === ErrorCode.FOREIGN_JOBSITES) ||
              _safe(
                () =>
                  Number(errors.slotOPError.error['slots'][0].requestProductStatusCode) === ErrorCode.FOREIGN_JOBSITES,
              ))
          ) {
            return true;
          } else {
            return false;
          }
        }),
      )
      .toPromise();
  }
  private async isForeignJobsiteDCO() {
    return this.store
      .select(getErrorSlotCode)
      .pipe(
        take(1),
        map((errorCode) => {
          if (Number(errorCode) === ErrorCode.FOREIGN_JOBSITES) {
            return true;
          } else {
            return false;
          }
        }),
      )
      .toPromise();
  }

  private async getSlumpProduct(formValues: { [fieldName: string]: any }) {
    return this.store
      .select(getData)
      .pipe(
        map(({ stateProducts, stateSlumps, experienceCode }) => {
          let selectedSlump: ImSlump;
          const productCode = formValues[SpecFieldNames.PRODUCT_SELECTION];
          const slumpCode = _safe(() => formValues[SpecFieldNames.SLUMP_PRODUCT]);
          if (
            experienceCode === ORDER_EXPERIENCES.COMMAND_UNIVERSAL_LINK ||
            experienceCode === ORDER_EXPERIENCES.SIMMA_UNIVERSAL_LINK
          ) {
            selectedSlump = _safe(() => stateSlumps[productCode].slumps.find((item) => item.slumpCode === slumpCode));
          } else {
            const techProd = _safe(
              () =>
                stateProducts.find((item) => item.productCode.includes(productCode)).technicalProducts[0]
                  .technicalProductCode,
            );
            selectedSlump = _safe(() => stateSlumps[techProd].slumps.find((item) => item.slumpCode === slumpCode));
          }
          return {
            slumpId: selectedSlump.slumpId,
            slumpCode: selectedSlump.slumpCode,
          };
        }),
        take(1),
      )
      .toPromise();
  }

  private async getSlump(formValues: { [fieldName: string]: any }) {
    return this.store
      .select(getSlumps)
      .pipe(
        map((slumps) => {
          const slumpCode = _safe(() => formValues[SpecFieldNames.SLUMP]);
          const selectedSlump = _safe(() => slumps.find((item) => item.slumpCode === slumpCode));
          return {
            slumpId: selectedSlump.slumpId,
            slumpCode: selectedSlump.slumpCode,
          };
        }),
        take(1),
      )
      .toPromise();
  }

  private async hasPump() {
    return this.store
      .select(getRmsRecipeSpecifications)
      .pipe(
        take(1),
        map((form) => _safe(() => form[0].values)),
        map((recipe) => {
          const pumpCodes = Object.keys(UnloadTypePumpCodesFrance).map((code) => UnloadTypePumpCodesFrance[code]);

          if (!!recipe && pumpCodes.includes(recipe.unloadType)) {
            return true;
          } else {
            return false;
          }
        }),
      )
      .toPromise();
  }

  private async hasMixVeyor() {
    return this.store
      .select(getRmsRecipeSpecifications)
      .pipe(
        take(1),
        map((form) => _safe(() => form[0].values)),
        map((recipe) => {
          const tapiCodes = Object.keys(UnloadTypeMixVeyorCodes).map((code) => UnloadTypeMixVeyorCodes[code]);
          if (!!recipe && tapiCodes.includes(recipe.unloadType)) {
            return true;
          }
          return false;
        }),
      )
      .toPromise();
  }

  private async isRawMaterial() {
    return this.store
      .select(getRmsSpecificationsRawMaterial)
      .pipe(
        take(1),
        map((hasRawMaterial) => {
          return hasRawMaterial;
        }),
      )
      .toPromise();
  }

  private async hasDeliveryBlock() {
    return this.store
      .select(getLegalEntity)
      .pipe(
        take(1),
        map((legalEntity) => {
          return legalEntity.block.deliveryBlock;
        }),
      )
      .toPromise();
  }
  /**
   * Constructs a `ProductUL` object based on experience code, country code, and optional temperature data.
   * @returns ProductUL
   */
  private prods(
    experienceCode: ORDER_EXPERIENCES,
    product,
    technicalProduct: TechnicalProductOP,
    selectedValueAddeds: (string | ValueAddedOP)[],
    temperatureData?: TemperatureData,
  ) {
    if (
      experienceCode === ORDER_EXPERIENCES.RMX_UNIVERSAL_LINK ||
      experienceCode === ORDER_EXPERIENCES.ARKIK_UNIVERSAL_LINK
    ) {
      return {
        productXId: product.productXId,
        productCode: product.productCode,
        productDesc: product.productDesc,
        customerProdDesc: product.customerProdDesc,
        technicalProduct: {
          technicalProductXId: technicalProduct ? technicalProduct.technicalProductXId : undefined,
          technicalProductCode: technicalProduct ? technicalProduct.technicalProductCode : undefined,
          valueAddeds: selectedValueAddeds ? selectedValueAddeds : undefined,
        },
      } as ProductUL;
    } else if (experienceCode === ORDER_EXPERIENCES.COMMAND_UNIVERSAL_LINK) {
      const refrigerant = !!temperatureData && !!temperatureData.refrigerant ? temperatureData.refrigerant : undefined;
      const technicalProductConf = !!temperatureData
        ? {
            technicalProductCode: '',
            valueAddeds: [temperatureData.summerSet, refrigerant, ...selectedValueAddeds].filter(
              (vap) => typeof vap !== 'string' && !!vap && !!vap.valueAddedCode,
            ),
          }
        : {
            technicalProductCode: '',
            valueAddeds: selectedValueAddeds,
          };

      const temperature =
        !!temperatureData && !!temperatureData.temperature
          ? {
              temperature: {
                temperatureId: temperatureData.temperature.temperatureId,
                temperatureCode: temperatureData.temperature.temperatureCode,
              },
            }
          : {};
      const aux = {
        productId: product['productId'],
        productCode: product.productCode,
        productDesc: product.productDesc,
        customerProdDesc: product.customerProdDesc,
        technicalProduct: technicalProductConf,
        ...temperature,
      };
      return aux as ProductUL;
    } else {
      return {
        productId: product['productId'],
        productCode: product.productCode,
        productDesc: product.productDesc,
        customerProdDesc: product.customerProdDesc,
      } as ProductUL;
    }
  }

  /**
   * Create clean up based on country code
   * RMX UL 5.2: clean up is based on the adjustment field
   * @param formValues
   * @param country
   * @returns boolean
   */
  private getCleanUp(formValues: { [fieldName: string]: any }, country: string) {
    if (country === COUNTRY_CODE.MEXICO) {
      return _safe(() => formValues[SpecFieldNames.ADJUSTMENT], false);
    }
    return _safe(
      () =>
        formValues[SpecFieldNames.CLEAN_UP] !== undefined
          ? formValues[SpecFieldNames.CLEAN_UP]
          : formValues[SpecFieldNames.ADJUSTMENT],
      false,
    );
  }

  private async getTemperatureData(formValues: { [fieldName: string]: any }) {
    const temperatureVaps$ = this.store.select(getTemperatureVaps);
    const temperatureValue$ = this.store.select(getTemperatures);
    const typeOfTemperature$ = this.experiencesService.isTemperatureEnabled$();
    const calendar$ = this.store.select(getFirstCalendar).pipe(
      filter((calendar) => !!calendar),
      take(1),
    );
    return await combineLatest([temperatureValue$, temperatureVaps$, typeOfTemperature$, calendar$])
      .pipe(
        filter(
          ([tempValues, temperaturesVaps, type]) =>
            (!!temperaturesVaps && !!temperaturesVaps[formValues.id]) || !!tempValues,
        ),
        take(1),
        map(([values, vaps, type, calendar]) => {
          if ((vaps && vaps[formValues.id] && vaps[formValues.id].length <= 0) || (values && values.length <= 0)) {
            return undefined;
          }

          const summerSetSelected =
            vaps[formValues.id] &&
            vaps[formValues.id].find((vap) => vap.valueAddedCode === formValues[SpecFieldNames.SUMMER_SET]);
          const isICE = type === TemperatureFeatures.ICE;
          const fliedName = isICE ? SpecFieldNames.ICE_QUANTITY : SpecFieldNames.TEMPERATURE_UNDER;
          const tempValue = values && values.find((temp) => temp.featureCode === formValues[fliedName]);

          if (isICE) {
            const refrigerant = _safe(
              () => getRefrigerantValueAdded(vaps[formValues.id], calendar?.tempControlFlag),
              undefined,
            );
            if (!!tempValue) {
              const tempData: TemperatureData = {
                refrigerant: {
                  valueAddedCode: refrigerant ? refrigerant.valueAddedCode.replace(/^(0+)/g, '') : undefined,
                  valueAddedQuantity: !!tempValue ? tempValue.featureValue : undefined,
                },
                temperature: {
                  temperatureId: !!tempValue ? tempValue.featureId : undefined,
                  temperatureCode: !!tempValue ? tempValue.featureCode : undefined,
                },
              };
              return tempData;
            } else {
              const tempData: TemperatureData = {
                temperature: {
                  temperatureId: undefined,
                  temperatureCode: undefined,
                },
              };
              return tempData;
            }
          } else {
            const refrigerant = _safe(
              () => getRefrigerantValueAdded(vaps[formValues.id], calendar?.tempControlFlag),
              undefined,
            );

            if (!!tempValue && tempValue.featureCode !== 'NONE') {
              const tempData: TemperatureData = {
                temperature: {
                  temperatureId: !!tempValue ? tempValue.featureId : undefined,
                  temperatureCode: !!tempValue ? tempValue.featureCode : undefined,
                },
                summerSet: {
                  valueAddedCode: summerSetSelected
                    ? summerSetSelected.valueAddedCode.replace(/^(0+)/g, '')
                    : undefined,
                },
                refrigerant: {
                  valueAddedCode: refrigerant ? refrigerant.valueAddedCode.replace(/^(0+)/g, '') : undefined,
                },
              };
              return tempData;
            } else {
              const tempData: TemperatureData = {
                temperature: {
                  temperatureId: !!tempValue ? tempValue.featureId : undefined,
                  temperatureCode: !!tempValue ? tempValue.featureCode : undefined,
                },
                summerSet: {
                  valueAddedCode: summerSetSelected
                    ? summerSetSelected.valueAddedCode.replace(/^(0+)/g, '')
                    : undefined,
                },
              };
              return tempData;
            }
          }
        }),
      )
      .toPromise();
  }

  private getDateTimePumpPrimerItem(dateAndTimeToCalculate: string, spacing: Spacing) {
    const spacingMinutes = _safe(() => spacing['spacingMinutes'], 0);
    // to current delivery date and time substract minutes selected in primer arrival field
    const deliveryDateTimeUpdated = _safe(() => moment(dateAndTimeToCalculate).add(-spacingMinutes, 'minutes'));
    return moment(_safe(() => _safe(() => deliveryDateTimeUpdated))).format('YYYY-MM-DD HH:mm');
  }

  /**
   * Calculate installation time with the RMX UL 5.2 fields
   * @param calendatState
   * @param recipe
   * @returns installationTime number
   */
  private getCalculatedInstallationTime(calendatState, recipe) {
    const pumpInstallTime = _safe(() => calendatState.pump.pumpInstallTime);
    const pipeInstallTime = _safe(() => calendatState.pump.pipeInstallTime);
    const pipeQuantity = _safe(() => recipe[SpecFieldNames.PIPE_QUANTITY], 0);
    const hoses = _safe(() => recipe[SpecFieldNames.HOSES], 0);

    let calculatedInstallationTime = 30;
    if (pumpInstallTime !== undefined && pipeInstallTime !== undefined) {
      calculatedInstallationTime = pumpInstallTime + pipeInstallTime * (pipeQuantity + hoses);
    }
    return calculatedInstallationTime;
  }

  private async getStartDate(formId, recipe) {
    const availableSlot = await this.getAvailableSlotDate();
    const dateTime = this.getDateTime(availableSlot, recipe[0].values);
    return this.store
      .select(getSlotForFormID(formId))
      .pipe(
        map((slots) => {
          const firstUnldSlot = _safe(
            () =>
              slots.response.slots[0].concreteProposals.find(
                (proposal) => proposal.deliveryDate === dateTime.date && proposal.arriveAtText === dateTime.time,
              ).firstUnldSlot,
          );
          const pumpingProposal = _safe(() =>
            slots.response.slots[0].pumpingProposals.find((proposal) => proposal.firstUnldSlot === firstUnldSlot),
          );

          const startDate = moment(pumpingProposal?.deliveryDate).format('YYYY-MM-DD');
          const startTime = pumpingProposal?.toSiteText;
          const unloadTypeValue = _safe(() => recipe[0].values[SpecFieldNames.UNLOAD_TYPE]) as string;
          const hasPump = isPump(unloadTypeValue);
          if (!!hasPump && pumpingProposal?.deliveryDate !== undefined && startTime !== undefined) {
            return `${startDate}T${startTime}`;
          } else {
            return '';
          }
        }),
        take(1),
      )
      .toPromise();
  }

  private async getEndDate(formId, recipe) {
    const availableSlot = await this.getAvailableSlotDate();
    const dateTime = this.getDateTime(availableSlot, recipe[0].values);
    return this.store
      .select(getSlotForFormID(formId))
      .pipe(
        map((slots) => {
          const loads = _safe(
            () =>
              slots.response.slots[0].concreteProposals.find(
                (proposal) => proposal.deliveryDate === dateTime.date && proposal.arriveAtText === dateTime.time,
              ).loads,
          );
          const unloadTypeValue = _safe(() => recipe[0].values[SpecFieldNames.UNLOAD_TYPE]) as string;
          const hasPump = isPump(unloadTypeValue);
          if (!!hasPump && loads !== undefined) {
            const lastLoad = loads[loads.length - 1];
            const date = moment(lastLoad.end).format('YYYY-MM-DD');
            const time = moment(lastLoad.end).format('HH:mm');
            return `${date}T${time}`;
          } else {
            return '';
          }
        }),
        take(1),
      )
      .toPromise();
  }

  private async getPumpAssigned(formId, recipe) {
    const availableSlot = await this.getAvailableSlotDate();
    const dateTime = this.getDateTime(availableSlot, recipe[0].values);
    return this.store
      .select(getSlotForFormID(formId))
      .pipe(
        map((slots) => {
          const firstUnldSlot = _safe(
            () =>
              slots.response.slots[0].concreteProposals.find(
                (proposal) => proposal.deliveryDate === dateTime.date && proposal.arriveAtText === dateTime.time,
              ).firstUnldSlot,
          );
          const pump = _safe(
            () =>
              slots.response.slots[0].pumpingProposals.find((proposal) => proposal.firstUnldSlot === firstUnldSlot)
                .pump,
          );
          const unloadTypeValue = _safe(() => recipe[0].values[SpecFieldNames.UNLOAD_TYPE]) as string;
          const hasPump = isPump(unloadTypeValue);
          if (!!hasPump && pump !== undefined) {
            return pump;
          } else {
            return '';
          }
        }),
        take(1),
      )
      .toPromise();
  }
  // TODO: update interface in cemex components so it has plant as it returns from BE
  private async getPlantFromOrderDetail() {
    return this.store
      .select(getNewModifyOrderState)
      .pipe(
        take(1),
        map((state) => state?.orderSelectedDetail?.order?.orderProducts[0]['plant']?.plantCode),
      )
      .toPromise();
  }

  async compareModifiedFields(experienceCode: ORDER_EXPERIENCES): Promise<string[]> {
    const orderState = await this.store
      .select(getNewModifyOrderState)
      .pipe(
        take(1),
        map((state) => state?.orderSelectedDetail?.order),
      )
      .toPromise();

    const spacingNotRequiredId = 85;
    const changedFields: string[] = [];

    const orderRequest = await this.createULOrderRequest(experienceCode);

    const orderProductItem = orderState?.orderProducts?.[0];
    const recipeItem = orderRequest?.items?.[0];

    const orderScheduledTime = moment(orderState?.programmedDateTime);
    const recipeScheduledTime = moment(orderRequest?.deliveryDate);

    const orderProductCode = orderProductItem?.productCode?.replace(/^0+/, '');
    const orderContractCode = orderProductItem?.contract?.contractCode?.replace(/^0+/, '');

    const dateChanged = !orderScheduledTime.isSame(recipeScheduledTime, 'day');
    const timeChanged = orderScheduledTime.format('HH:mm') !== recipeScheduledTime.format('HH:mm');

    const orderValueAddeds = orderProductItem['technicalProduct']?.valueAddeds ?? [];

    const recipeValueAddeds = recipeItem?.product?.technicalProduct?.valueAddeds ?? [];

    function compareAndPush(condition: boolean, fields: readonly string[]) {
      if (condition) {
        changedFields.push(...fields);
      }
    }

    function haveValueAddedsChanged(original: ValueAddedOP[], current: ValueAddedOP[]): boolean {
      if (original.length !== current.length) {
        return true;
      }

      return original.some(
        (orig, i) => !orig?.valueAddedCode?.includes(current[i]?.valueAddedCode?.replace(/^0+/, '')),
      );
    }

    const changedValueAdded = haveValueAddedsChanged(orderValueAddeds, recipeValueAddeds);

    compareAndPush(orderProductItem?.quantity !== recipeItem?.quantity, ModifiedFields.QUANTITY);
    compareAndPush(
      orderProductItem?.itemProperties?.spacing?.spacingId !== recipeItem?.spacing?.spacingId &&
        recipeItem?.spacing?.spacingId !== spacingNotRequiredId,
      ModifiedFields.SPACING,
    );
    compareAndPush(orderProductItem?.itemProperties['cleanUp'] !== recipeItem?.cleanUp, ModifiedFields.CLEAN_UP);
    compareAndPush(orderProductCode !== recipeItem?.product?.productCode, ModifiedFields.PRODUCT);
    compareAndPush(dateChanged, ModifiedFields.DELIVERY_DATE);
    compareAndPush(timeChanged, ModifiedFields.DELIVERY_TIME);
    compareAndPush(orderState?.contact?.contactId !== orderRequest?.contact?.contactId, ModifiedFields.CONTACT);
    compareAndPush(
      (orderState?.instructions?.replace(/[|]/g, '').trim() ?? '') !== (orderRequest?.instructions ?? ''),
      ModifiedFields.INSTRUCTIONS,
    );
    compareAndPush(orderState?.purchaseOrder !== orderRequest?.purchaseOrder, ModifiedFields.PURCHASE_ORDER);
    compareAndPush(
      orderProductItem?.itemProperties?.application?.applicationId !== recipeItem?.application?.applicationId,
      ModifiedFields.APPLICATION,
    );
    compareAndPush(
      orderProductItem?.itemProperties?.loadSize?.loadSizeId !== recipeItem?.loadSize?.loadSizeId,
      ModifiedFields.LOAD_SIZE,
    );
    compareAndPush(orderProductItem?.slump !== recipeItem?.slump?.slumpCode, ModifiedFields.SLUMP);
    compareAndPush(orderContractCode !== orderRequest?.contract?.contractCode, ModifiedFields.CONTRACT);
    compareAndPush(
      orderRequest?.pointOfDelivery?.pointOfDeliveryCode &&
        orderState?.pointOfDelivery?.pointOfDeliveryCode !== orderRequest?.pointOfDelivery?.pointOfDeliveryCode,
      ModifiedFields.POD,
    );
    const [orderPlant, recipePlant] = await Promise.all([this.getPlantFromOrderDetail(), this.getPlantDCO()]);
    compareAndPush(recipePlant !== undefined && orderPlant !== recipePlant, ModifiedFields.PLANT);
    compareAndPush(changedValueAdded, ModifiedFields.VALUE_ADDED);

    return changedFields;
  }
}
